import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Modal, Form } from 'react-bootstrap';
import { 
    useLazyGetScheduledEventsQuery,
    useAddScheduledEventMutation,
    useEditScheduledEventMutation,
    useDeleteScheduledEventMutation,
    useAllEmailsQuery
} from '../../app/api'; // Import your API hooks
import moment from 'moment';


// Integrated Calendar Component with API connectivity
const Calendar = () => {
    // Use RTK Query lazy hooks
    const [getEvents, { data: eventsData = [], isLoading: loading, error }] = useLazyGetScheduledEventsQuery();
    const {data: emailTemplates, refetch: refetch_emailTemplates} = useAllEmailsQuery();
    const [addEvent] = useAddScheduledEventMutation();
    const [updateEvent] = useEditScheduledEventMutation();
    const [deleteEvent] = useDeleteScheduledEventMutation();

    // State for events - separate from RTK Query result to allow immediate updates
    const [events, setEvents] = useState([]);

    // State for current view
    const [currentDate, setCurrentDate] = useState(moment());
    const [view, setView] = useState('month'); // 'month', 'week', 'day'

    // State for event modal
    const [showEventModal, setShowEventModal] = useState(false);
    const [selectedDate, setSelectedDate] = useState(null);
    const [selectedEvent, setSelectedEvent] = useState(null);
    // New event form state
    const [newEvent, setNewEvent] = useState({
        title: '',
        type: 'EmailSend', // default type
        date: '',
        time: '',
        description: '',
        templateid: 0
    });
    // udpate new event template id when we get the templates
    useEffect(()=>{
        if(emailTemplates && emailTemplates.length > 0){
            setNewEvent({
                ...newEvent,
                templateid: emailTemplates[0].id
            });
        }
    }, [emailTemplates])
  
  
  
  // Event types - you can expand this list
  const eventTypes = [
    { id: 'NewsLetter', label: 'NewsLetter', color: '#f8d7da' },
    { id: 'ActivitySummaries', label: 'Activity Summaries', color: '#d1e7dd' },
    { id: 'EmailSend', label: 'Email Send', color: '#cff4fc' },
    { id: 'Deadline', label: 'Deadline', color: '#fff3cd' }
  ];
  
  // Update local events state when RTK Query data changes
  useEffect(() => {
    if (eventsData && JSON.stringify(eventsData) !== JSON.stringify(events)) {
      setEvents(eventsData);
    }
  }, [eventsData]);
  
  // Load events when component mounts or when the date changes
  useEffect(() => {
    // Get start and end dates for the current view
    let startDate, endDate;
    
    if (view === 'month') {
      startDate = moment(currentDate).startOf('month').startOf('week').format('YYYY-MM-DD');
      endDate = moment(currentDate).endOf('month').endOf('week').format('YYYY-MM-DD');
    } else if (view === 'week') {
      startDate = moment(currentDate).startOf('week').format('YYYY-MM-DD');
      endDate = moment(currentDate).endOf('week').format('YYYY-MM-DD');
    } else {
      // day view
      startDate = moment(currentDate).format('YYYY-MM-DD');
      endDate = moment(currentDate).format('YYYY-MM-DD');
    }
    
    // Fetch events for the selected date range
    getEvents({ startDate, endDate });
  }, [getEvents, currentDate, view]);
  
  // Generate days for month view
  const generateMonthDays = () => {
    const startDay = moment(currentDate).startOf('month').startOf('week');
    const endDay = moment(currentDate).endOf('month').endOf('week');
    
    let days = [];
    let day = startDay.clone();
    
    while (day.isSameOrBefore(endDay, 'day')) {
      days.push(day.clone());
      day.add(1, 'day');
    }
    
    // Chunk days into weeks
    let weeks = [];
    while (days.length) {
      weeks.push(days.splice(0, 7));
    }
    
    return weeks;
  };
  
  // Filter events for a specific day
  const getEventsForDay = (day) => {
    return events.filter(event => 
      moment(event.date).format('YYYY-MM-DD') === day.format('YYYY-MM-DD')
    );
  };
  
  // Handle day click
  const handleDayClick = (day) => {
    // First close any open modal
    setShowEventModal(false);
    
    // Reset all form and selection state completely
    setSelectedEvent(null);
    
    // Wait for modal to close before showing the new one
    setTimeout(() => {
      setSelectedDate(day);
      setNewEvent({
        title: '',
        type: 'EmailSend',
        date: day.format('YYYY-MM-DD'),
        time: moment().format('HH:mm'),
        description: '',
        templateid: newEvent.templateid
      });
      setShowEventModal(true);
    }, 100);
  };
  
  // Handle event click
  const handleEventClick = (event, e) => {
    // Prevent event from bubbling up to the day click handler
    if (e) {
      e.stopPropagation();
    }
    
    // First close any open modal
    setShowEventModal(false);
    
    // Wait for modal to close before showing the new one
    setTimeout(() => {
      setSelectedEvent(event);
      setNewEvent({
        title: event.title,
        type: event.type || 'EmailSend',
        date: moment(event.date).format('YYYY-MM-DD'),
        time: moment(event.time, 'HH:mm').format('HH:mm'),
        description: event.description || '',
        templateid: event.templateid
      });
      setShowEventModal(true);
    }, 100);
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    const eventData = {
      ...newEvent,
      id: selectedEvent ? selectedEvent.id : Date.now().toString(),
      timestamp: moment(`${newEvent.date} ${newEvent.time}`).toISOString()
    };
    
    try {
      if (selectedEvent) {
        // Update existing event
        const result = await updateEvent(eventData).unwrap();
        
        // Update the event in local state immediately
        setEvents(events.map(event => 
          event.id === selectedEvent.id ? { ...eventData } : event
        ));
      } else {
        // Create new event
        const result = await addEvent(eventData).unwrap();
        // Add the new event to local state immediately
        setEvents([...events, eventData]);
      }
      
      // Close modal and reset form
      setShowEventModal(false);
      setSelectedEvent(null);
      setNewEvent(newEvent);
    } catch (err) {
      console.error('Failed to save event:', err);
      alert('An unexpected error occured while saving your event. please try again or contact an admin.');
    }
  };
  
  // Handle deleting an event
  const handleDeleteEvent = async () => {
    if (selectedEvent) {
      if( confirm('Are you sure you want to delete event: '+ selectedEvent.title) == true ){
        try {
          console.log(selectedEvent.id);
          await deleteEvent({'id':selectedEvent.id}).unwrap();
          
          // Remove the event from local state immediately
          setEvents(events.filter(event => event.id !== selectedEvent.id));
          
          setShowEventModal(false);
          setSelectedEvent(null);
        } catch (err) {
          console.error('Failed to delete event:', err);
          alert('An unexpected error occured while saving your event. please try again or contact an admin.');
        }
      }
    }
  };
  
  // Navigate to previous period based on current view
  const navigatePrevious = () => {
    switch(view) {
      case 'day':
        setCurrentDate(moment(currentDate).subtract(1, 'day'));
        break;
      case 'week':
        setCurrentDate(moment(currentDate).subtract(1, 'week'));
        break;
      default: // month view
        setCurrentDate(moment(currentDate).subtract(1, 'month'));
    }
  };
  
  // Navigate to next period based on current view
  const navigateNext = () => {
    switch(view) {
      case 'day':
        setCurrentDate(moment(currentDate).add(1, 'day'));
        break;
      case 'week':
        setCurrentDate(moment(currentDate).add(1, 'week'));
        break;
      default: // month view
        setCurrentDate(moment(currentDate).add(1, 'month'));
    }
  };
  
  // Navigate to today
  const goToToday = () => {
    setCurrentDate(moment());
  };
  
  // Change view (month, week, day)
  const changeView = (newView) => {
    setView(newView);
  };
  
  // Render header with navigation controls
  const renderHeader = () => {
    return (
      <Row className="mb-4">
        <Col md={4}>
          <Button variant="outline-primary" onClick={goToToday}>Today</Button>
          <Button variant="outline-secondary" className="mx-2" onClick={navigatePrevious}>
            &lt;
          </Button>
          <Button variant="outline-secondary" onClick={navigateNext}>
            &gt;
          </Button>
        </Col>
        <Col md={4} className="text-center">
          <h2>
            {view === 'day' ? currentDate.format('dddd, MMMM D, YYYY') : 
             view === 'week' ? `${moment(currentDate).startOf('week').format('MMM D')} - ${moment(currentDate).endOf('week').format('MMM D, YYYY')}` : 
             currentDate.format('MMMM YYYY')}
          </h2>
        </Col>
        <Col md={4} className="text-end">
          <Button 
            variant={view === 'month' ? 'primary' : 'outline-primary'} 
            className="mx-1"
            onClick={() => changeView('month')}
          >
            Month
          </Button>
          <Button 
            variant={view === 'week' ? 'primary' : 'outline-primary'} 
            className="mx-1"
            onClick={() => changeView('week')}
          >
            Week
          </Button>
          <Button 
            variant={view === 'day' ? 'primary' : 'outline-primary'} 
            className="mx-1"
            onClick={() => changeView('day')}
          >
            Day
          </Button>
        </Col>
      </Row>
    );
  };
  
  // Render month view
  const renderMonthView = () => {
    const weeks = generateMonthDays();
    
    return (
      <div className="calendar-month">
        <Row className="calendar-header">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, i) => (
            <Col key={i} className="text-center py-2 border">
              {day}
            </Col>
          ))}
        </Row>
        
        {weeks.map((week, weekIndex) => (
          <Row key={weekIndex} className="calendar-week">
            {week.map((day, dayIndex) => {
              const isCurrentMonth = day.month() === currentDate.month();
              const isToday = day.isSame(moment(), 'day');
              const dayEvents = getEventsForDay(day);
              
              return (
                <Col 
                  key={dayIndex} 
                  className={`calendar-day border p-0 ${isCurrentMonth ? '' : 'text-muted'} ${isToday ? 'bg-light' : ''}`}
                  style={{ height: '120px', overflow: 'hidden', cursor: 'pointer' }}
                  onClick={() => handleDayClick(day)}
                >
                  <div className="d-flex justify-content-between p-1">
                    <span>{day.format('D')}</span>
                    {isCurrentMonth && dayEvents.length > 0 && (
                      <small className="text-primary">{dayEvents.length} events</small>
                    )}
                  </div>
                  
                  <div className="event-list">
                    {isCurrentMonth && dayEvents.slice(0, 3).map((event, i) => {
                      const eventType = eventTypes.find(type => type.id === event.type) || eventTypes[0];
                      
                      return (
                        <div 
                          key={i}
                          className="event-item small p-1 mb-1 rounded"
                          style={{ backgroundColor: eventType.color }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEventClick(event, e);
                          }}
                        >
                          <div className="text-truncate">
                            <small>{moment(event.time, 'HH:mm').format('h:mm A')}</small> {event.title}
                          </div>
                        </div>
                      );
                    })}
                    
                    {isCurrentMonth && dayEvents.length > 3 && (
                      <div className="text-center">
                        <small>+{dayEvents.length - 3} more</small>
                      </div>
                    )}
                  </div>
                </Col>
              );
            })}
          </Row>
        ))}
      </div>
    );
  };
  
  // Render week view (simplified)
  const renderWeekView = () => {
    const startOfWeek = moment(currentDate).startOf('week');
    const weekDays = Array.from({ length: 7 }, (_, i) => moment(startOfWeek).add(i, 'days'));
    
    return (
      <div className="calendar-week-view">
        <Row className="calendar-header">
          <Col md={1} className="py-2 border text-center">Time</Col>
          {weekDays.map((day, i) => (
            <Col key={i} className="text-center py-2 border">
              <div>{day.format('ddd')}</div>
              <div>{day.format('MMM D')}</div>
            </Col>
          ))}
        </Row>
        
        {/* Render time slots (simplified) */}
        {Array.from({ length: 12 }, (_, i) => i + 8).map(hour => (
          <Row key={hour} className="calendar-hour border">
            <Col md={1} className="py-3 text-center border-end">
              {hour % 12 || 12}{hour < 12 ? 'AM' : 'PM'}
            </Col>
            
            {weekDays.map((day, dayIndex) => {
              const hourEvents = events.filter(event => {
                const eventMoment = moment(`${event.date} ${event.time}`);
                return eventMoment.isSame(day, 'day') && eventMoment.hour() === hour;
              });
              
              return (
                <Col 
                  key={dayIndex} 
                  className="py-1 px-2 border-end position-relative"
                  onClick={() => {
                    // First close any open modal
                    setShowEventModal(false);
                    
                    // Reset all form and selection state completely
                    setSelectedEvent(null);
                    
                    // Wait for modal to close before showing the new one
                    setTimeout(() => {
                      const selectedTime = moment(day).hour(hour).minute(0);
                      setSelectedDate(selectedTime);
                      setNewEvent({
                        title: '',
                        type: 'EmailSend',
                        date: selectedTime.format('YYYY-MM-DD'),
                        time: selectedTime.format('HH:mm'),
                        description: '',
                        templateid: newEvent.templateid
                      });
                      setShowEventModal(true);
                    }, 100);
                  }}
                >
                  {hourEvents.map((event, i) => {
                    const eventType = eventTypes.find(type => type.id === event.type) || eventTypes[0];
                    
                    return (
                      <div 
                        key={i}
                        className="event-item small p-1 mb-1 rounded"
                        style={{ backgroundColor: eventType.color }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEventClick(event, e);
                        }}
                      >
                        <div className="text-truncate">
                          {event.title}
                        </div>
                      </div>
                    );
                  })}
                </Col>
              );
            })}
          </Row>
        ))}
      </div>
    );
  };
  
  // Render day view (simplified)
  const renderDayView = () => {
    return (
      <div className="calendar-day-view">
        <Card className="mb-3">
          <Card.Header>
            <h3>{currentDate.format('dddd, MMMM D, YYYY')}</h3>
          </Card.Header>
          <Card.Body>
            {Array.from({ length: 24 }, (_, i) => i).map(hour => {
              const hourEvents = events.filter(event => {
                const eventMoment = moment(`${event.date} ${event.time}`);
                return eventMoment.isSame(currentDate, 'day') && eventMoment.hour() === hour;
              });
              
              return (
                <Row key={hour} className="py-2 border-bottom">
                  <Col md={1} className="text-end">
                    {hour % 12 || 12}{hour < 12 ? 'AM' : 'PM'}
                  </Col>
                  <Col 
                    md={11}
                    className="px-2 position-relative"
                    onClick={() => {
                      // First close any open modal
                      setShowEventModal(false);
                      
                      // Reset all form and selection state completely
                      setSelectedEvent(null);
                      
                      // Wait for modal to close before showing the new one
                      setTimeout(() => {
                        const selectedTime = moment(currentDate).hour(hour).minute(0);
                        setSelectedDate(selectedTime);
                        setNewEvent({
                          title: '',
                          type: 'EmailSend',
                          date: selectedTime.format('YYYY-MM-DD'),
                          time: selectedTime.format('HH:mm'),
                          description: '',
                          templateid: newEvent.templateid
                        });
                        setShowEventModal(true);
                      }, 100);
                    }}
                  >
                    {hourEvents.map((event, i) => {
                      const eventType = eventTypes.find(type => type.id === event.type) || eventTypes[0];
                      
                      return (
                        <div 
                          key={i}
                          className="event-item p-2 mb-1 rounded"
                          style={{ backgroundColor: eventType.color }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEventClick(event);
                          }}
                        >
                          <div>{event.title}</div>
                          <small>{moment(event.time, 'HH:mm').format('h:mm A')}</small>
                        </div>
                      );
                    })}
                  </Col>
                </Row>
              );
            })}
          </Card.Body>
        </Card>
      </div>
    );
  };
  
  // Render the selected view
  const renderCalendarView = () => {
    switch (view) {
      case 'week':
        return renderWeekView();
      case 'day':
        return renderDayView();
      default:
        return renderMonthView();
    }
  };
  
  // Render event modal
  const renderEventModal = () => {
    const modalTitle = selectedEvent ? 'Edit Event' : 'Add New Event';
    const buttonText = selectedEvent ? 'Update' : 'Save';
    return (
      <Modal 
        show={showEventModal} 
        onHide={() => {
          setShowEventModal(false);
          setSelectedEvent(null);
          setNewEvent(newEvent);
        }}
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {modalTitle}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Event Title</Form.Label>
              <Form.Control 
                type="text" 
                placeholder="Enter event title" 
                value={newEvent.title}
                onChange={(e) => setNewEvent({...newEvent, title: e.target.value})}
                required
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Event Type</Form.Label>
              <Form.Select 
                value={newEvent.type}
                onChange={(e) => setNewEvent({...newEvent, type: e.target.value})}
              >
                {eventTypes.map(type => (
                  <option key={type.id} value={type.id}>{type.label}</option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Email Template ID</Form.Label>
              <Form.Select 
                value={newEvent.templateid}
                onChange={(e) => setNewEvent({...newEvent, templateid: parseInt(e.target.value)})}
              >
                {emailTemplates && emailTemplates.map(templateid => (
                  <option key={templateid.id} value={templateid.id}>{templateid.id} - {templateid.title}</option>
                ))}
              </Form.Select>
            </Form.Group>
            
            <Row>
              <Form.Text className="text-muted">Server processes event using PACIFIC TIME.</Form.Text>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Date</Form.Label>
                  <Form.Control 
                    type="date" 
                    value={newEvent.date}
                    onChange={(e) => setNewEvent({...newEvent, date: e.target.value})}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Time</Form.Label>
                  <Form.Control 
                    type="time" 
                    value={newEvent.time}
                    onChange={(e) => setNewEvent({...newEvent, time: e.target.value})}
                    required
                  />
                  
                </Form.Group>
              </Col>
            </Row>
            
            <Form.Group className="mb-3">
              <Form.Label>Description (Optional)</Form.Label>
              <Form.Control 
                as="textarea" 
                rows={3}
                placeholder="Enter event description" 
                value={newEvent.description}
                onChange={(e) => setNewEvent({...newEvent, description: e.target.value})}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            {selectedEvent && (
              <Button variant="danger" className="me-auto" onClick={handleDeleteEvent}>
                Delete
              </Button>
            )}
            <Button 
              variant="secondary" 
              onClick={() => {
                setShowEventModal(false);
                setSelectedEvent(null);
                setNewEvent(newEvent);
              }}
            >
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              {buttonText}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    );
  };
  
  // Main render method
  return (
    <Container fluid className="calendar-container py-4" style={{maxWidth: '1200px'}}>
      {renderHeader()}
      
      {loading ? (
        <div className="text-center py-5">
          <p>Loading calendar events...</p>
        </div>
      ) : error ? (
        <div className="text-center py-5 text-danger">
          <p>Error loading calendar events: {error.message}</p>
        </div>
      ) : (
        renderCalendarView()
      )}
      
      {renderEventModal()}
    </Container>
  );
};

export default Calendar;